// "Añadir nueva marca" con la interfaz del onboarding (ADR-2026-10-02-marca-nueva-como-el-onboarding).
//
// Las mismas pantallas del onboarding (link, tablero, lista), en tres pasos y para una marca
// ADICIONAL: el escaneo va con `marca_nueva` (no toca la marca default ni la empresa) y
// confirmar guarda sólo la marca y la deja activa.

import { supabase } from '../supabase';
import { limpiarCacheDeProductos } from '../../pages/v2/productsCache';
import type { Tramo } from './dialogo';
import { enGuion, MARCA_DEL_GUION } from './guion';
import type { ColorDeMarca, TipografiasDeMarca } from './escaneo';

export const MARCA_NUEVA = {
  link: '/nueva-marca',
  marca: '/nueva-marca/marca',
  lista: '/nueva-marca/lista',
} as const;

export const PASOS_DE_MARCA_NUEVA = [MARCA_NUEVA.link, MARCA_NUEVA.marca, MARCA_NUEVA.lista] as const;

export const NOMBRE_DEL_PASO_DE_MARCA_NUEVA: Record<(typeof PASOS_DE_MARCA_NUEVA)[number], string> = {
  [MARCA_NUEVA.link]: 'Su web',
  [MARCA_NUEVA.marca]: 'Tu nueva marca',
  [MARCA_NUEVA.lista]: 'Lista',
};

/** ¿La pantalla es del flujo de marca nueva? (por la ruta: el link todavía no tiene estado). */
export function enMarcaNueva(pathname: string): boolean {
  return pathname === MARCA_NUEVA.link || pathname.startsWith(`${MARCA_NUEVA.link}/`);
}

/**
 * Lo que dice Cumbre en el link de una marca nueva. El detalle tiene que decir "la leo": ahí
 * saltan los stickers de lo que lee (`STICKERS_DE_MARCA_NUEVA_SALTAN_CON`).
 */
/** Lo que, escrito en el detalle de la marca nueva, arranca la cascada de stickers. */
export const STICKERS_DE_MARCA_NUEVA_SALTAN_CON = 'la leo';

export const DIALOGO_DE_MARCA_NUEVA: Tramo[] = [
  { texto: '¡Sumemos una marca!', estilo: 'saludo' },
  { texto: '¿Cuál es tu nueva marca?', estilo: 'pregunta' },
  {
    texto: 'Solo necesito el link de su web o de su tienda. Yo la leo, y la armo aparte de la que ya tienes.',
    estilo: 'detalle',
  },
];

export interface MarcaNuevaParaConfirmar {
  /** La fila que creó el escaneo; null si el análisis falló y hay que crearla con lo revisado. */
  id: string | null;
  nombre: string;
  rubro: string;
  colores: ColorDeMarca[];
  logoUrl: string | null;
  tipografias?: TipografiasDeMarca;
  analisis?: string;
  /** Con "Otro": cómo describió su rubro. Va a la descripción de ESTA marca
   *  (`user_description`, el "Sobre el negocio" que el agente lee de ella); la empresa no se
   *  toca (review #174). */
  rubroDetalle?: string;
}

/** La descripción de la marca con la del rubro al final (una sola vez). */
export function descripcionConRubro(descripcion: string | null | undefined, rubroDetalle: string | undefined): string | null {
  const base = descripcion?.trim() || '';
  const detalle = rubroDetalle?.trim();
  if (!detalle) return base || null;
  const nota = `Describe su rubro como: ${detalle}.`;
  if (base.includes(nota)) return base;
  return base ? `${base}\n\n${nota}` : nota;
}

/**
 * Confirma una marca ADICIONAL: guarda lo que la persona revisó en ESA fila (o la crea, si el
 * análisis falló) y la deja como la activa, como hace `RevisarMarca`. Nunca toca
 * `company_profiles`: la empresa y su marca de siempre quedan como estaban.
 */
export async function confirmarMarcaNueva(p: {
  companyId: string;
  marca: MarcaNuevaParaConfirmar;
}): Promise<{ ok: true; brandId: string } | { ok: false; error: string }> {
  if (enGuion()) return { ok: true, brandId: p.marca.id ?? MARCA_DEL_GUION };
  const nombre = p.marca.nombre.trim();
  if (!nombre || !p.marca.rubro) return { ok: false, error: 'Falta el nombre o el rubro de la marca.' };
  const datos = {
    name: nombre,
    rubro: p.marca.rubro,
    colors: p.marca.colores,
    logo_url: p.marca.logoUrl,
    is_draft: false,
  };

  let brandId = p.marca.id;
  if (brandId) {
    // La fila la creó el escaneo y puede traer su descripción: la del rubro se suma al final.
    let descripcion: string | null = null;
    if (p.marca.rubroDetalle?.trim()) {
      const { data, error: errLectura } = await supabase
        .from('brand_profiles')
        .select('user_description')
        .eq('id', brandId)
        .eq('id_company', p.companyId)
        .maybeSingle();
      // Sin poder leer lo que había, escribir pisaría la descripción entera con sólo la nota
      // del rubro (review #174): mejor que reintente.
      if (errLectura) return { ok: false, error: 'No pudimos guardar la marca. Intenta de nuevo.' };
      descripcion = descripcionConRubro(
        (data as { user_description?: string | null } | null)?.user_description,
        p.marca.rubroDetalle,
      );
    }
    const { error } = await supabase
      .from('brand_profiles')
      .update(descripcion ? { ...datos, user_description: descripcion } : datos)
      .eq('id', brandId)
      .eq('id_company', p.companyId);
    if (error) return { ok: false, error: 'No pudimos guardar la marca. Intenta de nuevo.' };
  } else {
    const descripcion = descripcionConRubro(null, p.marca.rubroDetalle);
    const { data, error } = await supabase
      .from('brand_profiles')
      .insert({
        ...datos,
        id_company: p.companyId,
        is_default: false,
        ...(p.marca.tipografias ? { fonts: p.marca.tipografias } : {}),
        ...(p.marca.analisis ? { context: p.marca.analisis } : {}),
        ...(descripcion ? { user_description: descripcion } : {}),
      })
      .select('id')
      .single();
    if (error || !data) return { ok: false, error: 'No pudimos crear la marca. Intenta de nuevo.' };
    brandId = (data as { id: string }).id;
  }

  // La marca recién creada pasa a ser la ACTIVA (ADR-2026-08-04-la-marca-nueva-arranca-vacia).
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) await supabase.from('profiles').update({ current_brand_id: brandId }).eq('id', user.id);
  } catch {
    /* best-effort: la marca ya quedó guardada; se puede elegir desde el selector */
  }
  // El catálogo cacheado es de la marca anterior: sin esto la home lo mostraría un instante.
  limpiarCacheDeProductos();
  window.dispatchEvent(new CustomEvent('atlas:brand-changed', { detail: { brandId } }));
  return { ok: true, brandId };
}
